import {
  infiniteQueryOptions,
  mutationOptions,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { UserWorkspace } from '@/api/types'
import { getSession } from '@/features/auth/session-store'
import { changeWorkspaceUnread } from '@/features/realtime/handlers/notifications'
import type { Notification } from './types'

export const NOTIFICATION_PAGE_SIZE = 20

interface NotificationPage {
  count: number
  next: string | null
  previous: string | null
  results: Notification[]
}

/**
 * One workspace's notifications, oldest page first.
 *
 * Paged rather than fetched whole: the list is unbounded and the panel shows a
 * screenful. The realtime layer marks this key stale rather than patching it --
 * see `features/realtime/handlers/core.ts` -- so a panel nobody has open costs a
 * flag and no request, and one that is open refetches what it holds.
 */
export function notificationsQuery(workspaceId: number) {
  return infiniteQueryOptions({
    queryKey: qk.notifications(workspaceId),
    queryFn: ({ pageParam }): Promise<NotificationPage> =>
      unwrap(
        getApi().GET('/api/notifications/{workspace_id}/', {
          params: {
            path: { workspace_id: workspaceId },
            query: { limit: NOTIFICATION_PAGE_SIZE, offset: pageParam },
          },
        }),
      ) as Promise<NotificationPage>,
    initialPageParam: 0,
    getNextPageParam: (last, _pages, lastOffset) =>
      last.next === null ? undefined : lastOffset + NOTIFICATION_PAGE_SIZE,
  })
}

export function useNotifications(workspaceId: number) {
  return useInfiniteQuery(notificationsQuery(workspaceId))
}

type Pages = InfiniteData<NotificationPage, number>

function patchPages(
  queryClient: QueryClient,
  workspaceId: number,
  update: (notifications: Notification[]) => Notification[],
): Pages | undefined {
  const key = qk.notifications(workspaceId)
  const previous = queryClient.getQueryData<Pages>(key)
  if (previous === undefined) return undefined

  queryClient.setQueryData<Pages>(key, {
    ...previous,
    pages: previous.pages.map((page) => ({ ...page, results: update(page.results) })),
  })
  return previous
}

/**
 * Applies the count changes an action of this user's implies.
 *
 * The acting tab has to do this itself. The server excludes the web socket id
 * that made the request from all three of these broadcasts, so no event comes
 * back to correct the badge -- and because it is excluded, there is no risk of
 * the arithmetic being applied twice either.
 */
function changeCounts(
  queryClient: QueryClient,
  workspaceId: number,
  workspaceDelta: number | 'zero',
  userDelta: number | 'zero',
): void {
  queryClient.setQueryData<UserWorkspace[]>(qk.workspaces(), (workspaces) => {
    if (workspaces === undefined) return workspaces
    if (workspaceDelta === 'zero') {
      return workspaces.map((workspace) =>
        workspace.id === workspaceId
          ? { ...workspace, unread_notifications_count: 0 }
          : workspace,
      )
    }
    return changeWorkspaceUnread(workspaces, workspaceId, workspaceDelta)
  })

  const session = getSession()
  session.setUnreadNotificationCount(
    userDelta === 'zero' ? 0 : session.unreadNotificationCount + userDelta,
  )
}

export function markNotificationReadMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: ({
      workspaceId,
      notificationId,
    }: {
      workspaceId: number
      notificationId: number
    }) =>
      unwrap(
        getApi().PATCH('/api/notifications/{workspace_id}/{notification_id}/', {
          params: {
            path: { workspace_id: workspaceId, notification_id: notificationId },
          },
        }),
      ),
    onMutate: ({ workspaceId, notificationId }) => {
      const target = findNotification(queryClient, workspaceId, notificationId)
      // Already read is not an error and not a no-op request -- the panel marks
      // on click -- but it must not decrement anything a second time.
      const unread = target !== undefined && target.read !== true

      const previous = patchPages(queryClient, workspaceId, (notifications) =>
        notifications.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification,
        ),
      )

      if (unread) {
        // A notification with no workspace belongs to the user, and its count
        // lives on the session rather than on any workspace.
        const belongsToWorkspace = target.workspace !== null
        changeCounts(
          queryClient,
          workspaceId,
          belongsToWorkspace ? -1 : 0,
          belongsToWorkspace ? 0 : -1,
        )
      }

      return { previous, unread, workspaceOwned: target?.workspace !== null }
    },
    onError: (_error, { workspaceId }, context) => {
      restore(queryClient, workspaceId, context?.previous)
      if (context?.unread !== true) return
      changeCounts(
        queryClient,
        workspaceId,
        context.workspaceOwned ? 1 : 0,
        context.workspaceOwned ? 0 : 1,
      )
    },
  })
}

export function useMarkNotificationRead() {
  return useMutation(markNotificationReadMutation(useQueryClient()))
}

export function markAllNotificationsReadMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (workspaceId: number) =>
      unwrap(
        getApi().POST('/api/notifications/{workspace_id}/mark-all-as-read/', {
          params: { path: { workspace_id: workspaceId } },
        }),
      ),
    onMutate: (workspaceId) => {
      const previous = patchPages(queryClient, workspaceId, (notifications) =>
        notifications.map((notification) => ({ ...notification, read: true })),
      )
      const previousUserCount = getSession().unreadNotificationCount
      const previousWorkspaces = queryClient.getQueryData<UserWorkspace[]>(
        qk.workspaces(),
      )

      // Both counts, because the endpoint marks this workspace's notifications
      // and the user's own -- it filters `workspace_id = <id> OR IS NULL`.
      changeCounts(queryClient, workspaceId, 'zero', 'zero')
      return { previous, previousUserCount, previousWorkspaces }
    },
    onError: (_error, workspaceId, context) => {
      restore(queryClient, workspaceId, context?.previous)
      restoreCounts(queryClient, context)
    },
  })
}

export function useMarkAllNotificationsRead() {
  return useMutation(markAllNotificationsReadMutation(useQueryClient()))
}

export function clearNotificationsMutation(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (workspaceId: number) =>
      unwrap(
        getApi().DELETE('/api/notifications/{workspace_id}/', {
          params: { path: { workspace_id: workspaceId } },
        }),
      ),
    onMutate: (workspaceId) => {
      const previous = patchPages(queryClient, workspaceId, () => [])
      const previousUserCount = getSession().unreadNotificationCount
      const previousWorkspaces = queryClient.getQueryData<UserWorkspace[]>(
        qk.workspaces(),
      )

      changeCounts(queryClient, workspaceId, 'zero', 'zero')
      return { previous, previousUserCount, previousWorkspaces }
    },
    onError: (_error, workspaceId, context) => {
      restore(queryClient, workspaceId, context?.previous)
      restoreCounts(queryClient, context)
    },
  })
}

export function useClearNotifications() {
  return useMutation(clearNotificationsMutation(useQueryClient()))
}

function findNotification(
  queryClient: QueryClient,
  workspaceId: number,
  notificationId: number,
): Notification | undefined {
  const pages = queryClient.getQueryData<Pages>(qk.notifications(workspaceId))
  return pages?.pages
    .flatMap((page) => page.results)
    .find((notification) => notification.id === notificationId)
}

function restore(
  queryClient: QueryClient,
  workspaceId: number,
  previous: Pages | undefined,
): void {
  if (previous !== undefined) {
    queryClient.setQueryData(qk.notifications(workspaceId), previous)
  }
}

function restoreCounts(
  queryClient: QueryClient,
  context:
    | { previousUserCount: number; previousWorkspaces: UserWorkspace[] | undefined }
    | undefined,
): void {
  if (context === undefined) return
  getSession().setUnreadNotificationCount(context.previousUserCount)
  if (context.previousWorkspaces !== undefined) {
    queryClient.setQueryData(qk.workspaces(), context.previousWorkspaces)
  }
}
